import { RouteObject } from "react-router-dom";
import { Navigate } from "react-router-dom";
import { AdminPage } from "@/pages/admin";
import { DebugIndexPage, DebugDbPage, DebugDbReplPage } from "@/pages/debug";
import { RequireAdminAuthOutlet } from "@/app/outlets/AdminOutlet";
import { RequireDebugModeOutlet } from "@/app/outlets/RequireDebugModeOutlet";
import { TrashPage } from "@/pages/trash";
import { CollectionsListPage } from "@/pages/collections";
import { CollectionsViewPage } from "@/pages/collections-view";
import { IntegrationsPage, IntegrationsApiPage } from "@/pages/integrations";
import { SearchPage } from "@/pages/search";
import { SettingsLayoutOutlet } from "@/app/outlets/SettingsLayoutOutlet";
import {
  SettingsEmailPreferencesPage,
  SettingsAccountManagementPage,
  SettingsSubscriptionManagementPage,
  SettingsExportsPage,
  SettingsDebugAccountPage,
} from "@/pages/settings";
import { NotFoundPage } from "@/pages/not-found";
import { appRoutes } from "@/app/router/appRoutes";
import { RequireStandardModeOutlet } from "@/app/outlets/RequireStandardModeOutlet";
import { StandardModePadding } from "@/app/outlets/StandardModePadding";
import { NotesListPage } from "@/pages/notes/NotesListPage";
import { SettingsAccountMigrationPage } from "@/pages/settings/SettingsAccountMigrationPage";
import { SettingsDebugSandboxPage } from "@/pages/settings/SettingsDebugSandboxPage";
import { ExplorePage } from "@/pages/explore/ExplorePage";
import TopicDetailPage from "@/pages/explore/TopicDetailPage";
import { TemplatesListPage } from "@/pages/templates-list/TemplatesListPage";
import { TemplatesViewPage } from "@/pages/templates-view/TemplatesViewPage";
import { V1MigrationPage } from "@/pages/v1-migration/V1MigrationPage";
import { SessionsListPage } from "@/pages/sessions-list";
import { SessionsViewPage } from "@/pages/sessions-view";
import {
  AudioRecordingsPage,
  AudioRecordingDetailPage,
  AudioRecordingCreatePage,
} from "@/pages/audio-recordings";
import { IntegrationsEmailPage } from "@/pages/integrations/email/integrations-email-page";
import { IntegrationsTextMessagePage } from "@/pages/integrations/text-message/integrations-text-message-page";
import { OAuthConsentPage } from "@/pages/oauth-consent/oauth-consent-page";
import { IntegrationsZapierPage } from "@/pages/integrations/zapier/integrations-zapier-page";
import { ImportedNotesListPage } from "@/pages/notes/ImportedNotesListPage";
import { ImportedCollectionsListPage } from "@/pages/collections/ImportedCollectionsListPage";
import { StandardNotesViewPage } from "@/pages/notes-view/NotesViewPage";
import { IntegrationsChromeExtensionPage } from "@/pages/integrations/chrome-extension/integrations-chrome-extension-page";

export const STANDARD_MODE_ROUTES: RouteObject[] = [
  {
    path: appRoutes.root({}).path,
    element: <RequireStandardModeOutlet />,
    children: [
      /**
       * =============================================
       *               V1 Migrated Routes
       * =============================================
       */
      {
        path: appRoutes.v1MigrationGroups({ starPattern: true }).path,
        element: <V1MigrationPage />,
      },
      {
        path: appRoutes.v1MigrationNotes({ starPattern: true }).path,
        element: <V1MigrationPage />,
      },
      {
        path: appRoutes.v1MigrationTemplates({ starPattern: true }).path,
        element: <V1MigrationPage />,
      },
      /**
       * =============================================
       *               OAuth Routes
       * =============================================
       */
      {
        path: appRoutes.oauthConsent({}).path,
        element: <OAuthConsentPage />,
      },
      /**
       * =============================================
       *               Notes Routes
       * =============================================
       */
      {
        path: appRoutes.notesView({
          params: { noteId: ":noteId" },
        }).path,
        element: <StandardNotesViewPage />,
      },
      {
        path: appRoutes.notesList({}).path,
        element: <NotesListPage />,
      },
      {
        path: appRoutes.home({}).path,
        element: <Navigate to={appRoutes.notesList({}).path} />,
      },
      {
        path: appRoutes.everything({}).path,
        element: <Navigate to={appRoutes.notesList({}).path} />,
      },
      /**
       * =============================================
       *               Import Routes
       * =============================================
       */
      {
        path: appRoutes.importBatchNotes({
          params: { importBatchId: ":importBatchId" },
        }).path,
        element: <ImportedNotesListPage />,
      },
      {
        path: appRoutes.importBatchCollections({
          params: { importBatchId: ":importBatchId" },
        }).path,
        element: <ImportedCollectionsListPage />,
      },
      /**
       * =============================================
       *               Trash Routes
       * =============================================
       */
      {
        path: appRoutes.trash({}).path,
        element: <TrashPage />,
      },
      /**
       * =============================================
       *               Session Routes
       * =============================================
       */
      {
        path: appRoutes.sessionsList({}).path,
        element: <SessionsListPage />,
      },
      {
        path: appRoutes.sessionsView({
          params: { sessionId: ":sessionId" },
        }).path,
        element: <SessionsViewPage />,
      },
      {
        path: appRoutes.audioRecordings({}).path,
        element: <AudioRecordingsPage />,
      },
      {
        path: appRoutes.audioRecordingCreate({}).path,
        element: <AudioRecordingCreatePage />,
      },
      {
        path: appRoutes.audioRecording({
          params: { recordingId: ":recordingId" },
        }).path,
        element: <AudioRecordingDetailPage />,
      },
      /**
       * =============================================
       *               Settings Routes
       * =============================================
       */
      {
        path: appRoutes.settings({}).path,
        element: <SettingsLayoutOutlet />,
        children: [
          {
            path: appRoutes.settingsEmailPreferences({}).path,
            element: <SettingsEmailPreferencesPage />,
          },
          {
            path: appRoutes.settingsSubscriptionManagement({}).path,
            element: <SettingsSubscriptionManagementPage />,
          },
          {
            path: appRoutes.settingsAccountManagement({}).path,
            element: <SettingsAccountManagementPage />,
          },
          {
            path: appRoutes.settingsExports({}).path,
            element: <SettingsExportsPage />,
          },
          {
            path: appRoutes.settingsAccountMigration({}).path,
            element: <SettingsAccountMigrationPage />,
          },
          {
            path: appRoutes.settingsDebugAccount({}).path,
            element: <SettingsDebugAccountPage />,
          },
          {
            path: appRoutes.settingsDebugSandbox({}).path,
            element: <SettingsDebugSandboxPage />,
          },
          {
            path: appRoutes.settingsDebug({}).path,
            element: <Navigate to={appRoutes.settingsDebugAccount({}).path} />,
          },
          {
            path: appRoutes.settings({}).path,
            element: <Navigate to={appRoutes.settingsEmailPreferences({}).path} />,
          },
        ],
      },
      /**
       * =============================================
       *               Explore Routes
       * =============================================
       */
      {
        path: appRoutes.explore({}).path,
        element: <ExplorePage />,
      },
      {
        path: appRoutes.exploreTopicDetail({
          params: { topicId: ":topicId" },
        }).path,
        element: <TopicDetailPage />,
      },
      /**
       * =============================================
       *               Collections Routes
       * =============================================
       */
      {
        path: appRoutes.collections({}).path,
        element: <CollectionsListPage />,
      },
      {
        path: appRoutes.collectionsView({
          params: { collectionId: ":collectionId" },
        }).path,
        element: <CollectionsViewPage />,
      },
      /**
       * =============================================
       *               Templates Routes
       * =============================================
       */
      {
        path: appRoutes.templatesList({}).path,
        element: <TemplatesListPage />,
      },
      {
        path: appRoutes.templatesView({
          params: { templateId: ":templateId" },
        }).path,
        element: <TemplatesViewPage />,
      },
      /**
       * =============================================
       *               Integrations Routes
       * =============================================
       */
      {
        path: appRoutes.integrations({}).path,
        element: <IntegrationsPage />,
      },

      {
        path: appRoutes.integrationsApi({}).path,
        element: <IntegrationsApiPage />,
      },

      {
        path: appRoutes.integrationsZapier({}).path,
        element: <IntegrationsZapierPage />,
      },

      {
        path: appRoutes.integrationsEmail({}).path,
        element: <IntegrationsEmailPage />,
      },

      {
        path: appRoutes.integrationsTextMessage({}).path,
        element: <IntegrationsTextMessagePage />,
      },

      {
        path: appRoutes.integrationsChromeExtension({}).path,
        element: <IntegrationsChromeExtensionPage />,
      },

      /**
       * =============================================
       *               Search Routes
       * =============================================
       */
      {
        path: appRoutes.search({}).path,
        element: <SearchPage />,
      },

      /**
       * =============================================
       *               Admin Routes
       * =============================================
       */
      {
        path: appRoutes.admin({}).path,
        element: <RequireAdminAuthOutlet />,
        children: [
          {
            path: appRoutes.admin({}).path,
            element: <AdminPage />,
          },
        ],
      },

      {
        path: appRoutes.debug({}).path,
        element: <RequireDebugModeOutlet />,
        children: [
          {
            path: appRoutes.debug({}).path,
            element: <DebugIndexPage />,
          },
          {
            path: appRoutes.debugDb({}).path,
            element: <DebugDbPage />,
          },
          {
            path: appRoutes.debugDbRepl({}).path,
            element: <DebugDbReplPage />,
          },
        ],
      },
    ],
  },
  {
    path: appRoutes.root({ starPattern: true }).path,
    element: (
      // Apply consistent padding for the NotFoundPage.
      <StandardModePadding>
        <NotFoundPage />
      </StandardModePadding>
    ),
  },
];
